import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ChatTextType,
  MuteSettingsType,
  UpdateBlockStatusParams,
  UpdateConversationSettingsParams,
} from "../../../../libraries/types_interface/chats/ChatsTypes";
import CacheStorage from "../../../../service/storage/CacheStorage";
import StorageKeys from "../../../../const/StorageKeys";
import SocketService, {
  socket,
} from "../../../../service/socket/SocketService";
import SessionStorage from "../../../../service/storage/SessionStorage";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import { ConversationList } from "../../../../model/chats/ChatListModel";
import SocketChatsKeys from "../../../../const/SocketChatsKeys";
import {
  DepartmentConversationDetailsModel,
  DepartmentMutedConversations,
  DepartmentResults,
} from "../../../../model/chats/DepartmentConversationDetailsModel";
import {
  GroupConversationMessageModel,
  Message,
} from "../../../../model/chats/GroupConversationMessageModel";
import DepartmentChatsProvider from "./DepartmentChatsProvider";
import EncryptDecryptService, {
  GroupMessageEncryptedType,
  HybridEncrypted,
} from "../../../../service/encrypt_decrypt/EncryptDecryptService";
import ChatsProvider from "../chats/ChatsProvider";
import { toLocalDate } from "../../../../service/date_service/date_service";
import { useDispatch } from "react-redux";
import { ResponsiveDeviceType } from "../../../../widget/layouts/ScreenMedia";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import {
  cancelChatARF,
  chatRefreshListStatus,
  mediaDeleteChooseAction,
  messageDeleteChooseAction,
  sendChatMessage,
  sendForwardMessages,
  setForwardMediaList,
} from "../../../../libraries/state_reducers/chats/ChatsReducer";
import {
  addModal,
  removeModal,
} from "../../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../../const/ModalKeys";
import Loader from "../../../../widget/common/ui/Loader";
import { UploadEncryptedMedia } from "../../../../model/chats/ChatMediaUploadedDetailModel";
import ImagePickerPreviewProvider from "../../../widget/ImagePickerPreviewProvider";
import {
  audioExtensions,
  docExtensions,
  imageExtensions,
  pdfExtensions,
  videoExtensions,
} from "../../../../const/FileExtensions";
import {
  setARFFormType,
  submitARFDetail,
} from "../../../../libraries/state_reducers/chats/ARFReducer";
import { isMobile } from "react-device-detect";
import { chatsARFMessageTypes } from "../../../../const/ChatsConstant";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import { ReplyMessageDetailModel } from "../../../../model/chats/ReplyMessageDetailModel";
import { ForwardMessageList } from "../../../../model/chats/ForwardProfileListModel";

type DepartmentChatsContextType = {
  getSelectedChatSession: () => void;
  resetMessages: () => void;
  disconnectCallBacks: () => void;
  conversationCallBack: (eventName: string, ...args: any[]) => void;
  updateConversationSettings: (
    detail: UpdateConversationSettingsParams
  ) => void;
  updateMuteSettings: () => void;

  userId: string;
  conversationMessages: Record<string, Array<Message>> | null;
  conversationDetail: DepartmentResults | null;
  muteSettings: MuteSettingsType;

  typingStatus: boolean;
  recordingStatus: boolean;

  // CHATTING
  sendTextMessage: (msg: string, callback: () => void) => void;
  sendAudioMessage: (
    file: File,
    secondsDuration: number,
    callback: () => void
  ) => void;
  sendMediaMessage: (
    file: File,
    secondsDuration: number,
    callback: () => void
  ) => void;

  // OPTION
  handleClickMessage: (message: Message) => void;
  handlePressStart: (message: Message) => void;
  handlePressEnd: () => void;
  clearLongPress: () => void;
  selectedMesasgeIds: Array<string>;
  selectedMessages: Array<Message>;
  longPressStatus: boolean;

  replyMessage: ReplyMessageDetailModel | null;
  longPressAction: (action: "reply" | "copy" | "delete" | "forward") => void;
  cancelReply: () => void;
};

const DepartmentChatsContext = createContext<
  DepartmentChatsContextType | undefined
>(undefined);

export const DepartmentChatsContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const type: number = ResponsiveDeviceType();

  const muteDurationDepartmentSelector = useSelector(
    (state: RootState) => state.chats.muteDurationDepartment
  );

  const arfFormDetailSelector = useSelector(
    (state: RootState) => state.ARF.formDetail
  );

  const sendMessageDetailSelector = useSelector(
    (state: RootState) => state.chats.sendMessageDetail
  );

  const cancelARFDetailSelector = useSelector(
    (state: RootState) => state.chats.cancelARFDetail
  );

  const messageDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.messageDeleteActionType
  );

  const mediaDeleteActionTypeSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteActionType
  );

  const mediaDeleteEveryoneSelector = useSelector(
    (state: RootState) => state.chats.mediaDeleteEveryone
  );

  const forwardedMediaListSelector = useSelector(
    (state: RootState) => state.chats.forwardedMediaList
  );

  const forwardedMessagesSelector = useSelector(
    (state: RootState) => state.chats.forwardedMessages
  );

  const voiceLengthRef = useRef<string>("00:00");

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const longPressStatusRef = useRef<boolean>(false);

  const userId = CacheStorage.getValue(StorageKeys.account_id) || "0";
  const privateKeyRef = useRef<string>(
    SessionStorage.getValue(StorageKeys.chats_symetric_key) || ""
  );
  /* const publicKeyRef = useRef<string>(
    CacheStorage.getValue(StorageKeys.public_key) || ""
  ); */

  const chatSelectedRef = useRef<ConversationList | null>(null);
  const conversationDetailRef = useRef<DepartmentResults | null>(null);
  const selectedMesasgeIdsRef = useRef<Array<string>>([]);

  const conversationMessagesRef = useRef<Array<Message> | null>(null);
  const muteSettingsRef = useRef<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [typingStatus, setTypingStatus] = useState<boolean>(false);
  const [recordingStatus, setRecordingStatus] = useState<boolean>(false);

  const [conversationDetail, setConversationDetail] =
    useState<DepartmentResults | null>(null);

  const [conversationMessages, setConversationMessages] = useState<Record<
    string,
    Message[]
  > | null>(null);

  const [muteSettings, setMuteSettings] = useState<MuteSettingsType>(
    ChatsProvider.muteSettingsDefault
  );

  const [longPressStatus, setLongPressStatus] = useState<boolean>(false);
  const [selectedMesasgeIds, setSelectedMesasgeIds] = useState<Array<string>>(
    []
  );
  const [selectedMessages, setSelectedMessages] = useState<Array<Message>>([]);
  const [replyMessage, setReplyMessage] =
    useState<ReplyMessageDetailModel | null>(null);

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const resetValues = () => {
    privateKeyRef.current =
      SessionStorage.getValue(StorageKeys.chats_symetric_key) || "";
    chatSelectedRef.current = null;

    setTypingStatus(false);
    setRecordingStatus(false);

    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;
    setMuteSettings(ChatsProvider.muteSettingsDefault);

    resetMessages();
    setReplyMessage(null);
  };

  const clearLongPress = () => {
    longPressStatusRef.current = false;
    setLongPressStatus(false);

    setSelectedMesasgeIds([]);
    selectedMesasgeIdsRef.current = [];
    setSelectedMessages([]);
  };

  const resetMessages = () => {
    conversationDetailRef.current =
      ChatsProvider.getChatConversationDetailSession();
    conversationMessagesRef.current = null;

    setConversationDetail(conversationDetailRef.current);
    setConversationMessages(conversationMessagesRef.current);

    clearLongPress();
  };

  const cancelReply = () => {
    clearLongPress();
    setReplyMessage(null);
  };

  // GET SESSION SELECTED CHAT -> getting messages ////////////////////////////////////////////
  const getSelectedChatSession = async (
    getConversDetailStatus: boolean = true
  ) => {
    if (socket.connected) {
      const chatsChatSelectedSession = SessionStorage.getValue(
        StorageKeys.chats_chat_selected
      );

      if (UtilityHelper.isNotEmpty(chatsChatSelectedSession)) {
        const chatSelected: ConversationList = JSON.parse(
          chatsChatSelectedSession
        );

        chatSelectedRef.current = chatSelected;

        if (getConversDetailStatus) {
          SocketService.initConversation({
            flag: chatSelected?.flag!,
            eventN:
              chatSelected?.flag! === "3"
                ? SocketChatsKeys.join_chat_group
                : SocketChatsKeys.join_chat,
            convId: chatSelected?.conv_id!,
            //socketCallBack: conversationCallBack,
            callback: (response: any) => {
              if (response["status"] === "1") {
                const departmentConversationDetailsModel: DepartmentConversationDetailsModel =
                  response;
                const symetricKey: string =
                  departmentConversationDetailsModel?.results?.symmetric_key!;

                conversationDetailRef.current =
                  departmentConversationDetailsModel?.results!;
                setConversationDetail(conversationDetailRef.current);

                SessionStorage.saveValue(
                  StorageKeys.chats_symetric_key,
                  symetricKey
                );

                privateKeyRef.current = symetricKey;

                ChatsProvider.setupChatConversationDetail({
                  flag: chatSelectedRef.current?.flag!,
                  detail: conversationDetailRef.current,
                });

                // UPDATE SESSION CONVERS DETAIL
                SessionStorage.saveValue(
                  StorageKeys.chats_chat_selected,
                  JSON.stringify({
                    ...chatSelected,
                    department_id:
                      conversationDetailRef.current?.department_id!,
                  })
                );

                if (conversationDetailRef.current?.access_form_status !== "0") {
                  handleShowARF();
                }

                updateMuteSettings();
              }
            },
          });
        }
      }
    }
  };

  const handleShowARF = () => {
    if (conversationDetailRef.current != null) {
      dispatch(
        setARFFormType(
          conversationDetailRef.current?.form_status! as "1" | "2" | "3"
        )
      );
      dispatch(
        addModal({
          id: ModalKeys.arf_form,
          size: "lg",
          title: UtilityHelper.ARFLabel(
            conversationDetailRef.current?.form_status!
          ),
          screenMobileView: type === 3 ? false : true,
        })
      );
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateMuteSettings = () => {
    const mutedConversations: DepartmentMutedConversations =
      conversationDetailRef.current?.conv_settings?.muted_conversations!;
    muteSettingsRef.current = ChatsProvider.muteSettingsDefault;

    muteSettingsRef.current = {
      ...muteSettingsRef.current,
      muteStatus:
        conversationDetailRef.current?.conv_settings?.muted_conversations
          ?.mute_status!,
    };

    if (mutedConversations?.mute_status! === "1") {
      muteSettingsRef.current = {
        ...muteSettingsRef.current,
        muteDuration: mutedConversations?.mute_duration!,
        muteEnd:
          mutedConversations.mute_type! === "1"
            ? toLocalDate(
                new Date(mutedConversations?.mute_end!),
                "d MMMM, h:mm a",
                true
              )
            : "",
      };
    }

    setMuteSettings(muteSettingsRef.current);
  };

  const submitMuteDetail = (muteStatusVal: string) => {
    console.log(`submitMuteDetail: ${muteStatusVal}`);
    if (["1", "2", "3", "4", "5"].includes(muteStatusVal)) {
      const muteSettingsTemp: MuteSettingsType = ChatsProvider.updateMuteStatus(
        Number(muteStatusVal),
        muteSettingsRef.current
      );

      updateConversationSettings({
        convId: chatSelectedRef?.current?.conv_id!,
        eventName: SocketChatsKeys.chat_mute_notification,
        muteDuration: muteSettingsTemp?.muteDuration,
        muteStatus: muteSettingsTemp?.muteStatus,
        muteType: muteSettingsTemp?.muteType,
      });
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateBlockStatus = ({
    blockStatusVal,
    participantBlockStatusVal,
    participantBlockStatus,
    accountIdVal,
    profileId,
  }: UpdateBlockStatusParams) => {
    console.log(
      `updateBlockStatus: ${participantBlockStatus} ${userId} ${accountIdVal} ${profileId} `
    );
    let conversationDetailTemp: DepartmentResults =
      conversationDetailRef.current!;

    if (userId === accountIdVal) {
      if (participantBlockStatus) {
        if (
          conversationDetailTemp.conv_settings?.block_details
            ?.participant_block_status !== undefined
        ) {
          conversationDetailTemp = {
            ...conversationDetailTemp,
            conv_settings: {
              ...conversationDetailTemp.conv_settings,
              block_details: {
                ...conversationDetailTemp.conv_settings.block_details,
                participant_block_status: participantBlockStatusVal,
              },
            },
          };
        }
      } else {
        if (
          conversationDetailTemp?.conv_settings?.block_details
            ?.my_block_status !== undefined
        ) {
          conversationDetailTemp = {
            ...conversationDetailTemp,
            conv_settings: {
              ...conversationDetailTemp.conv_settings,
              block_details: {
                ...conversationDetailTemp.conv_settings.block_details,
                my_block_status: blockStatusVal,
              },
            },
          };
        }
      }
    }

    conversationDetailRef.current = conversationDetailTemp;
    setConversationDetail(conversationDetailTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateDeleteStatusFromOther = (msgIds: string) => {
    const messagesIds: Array<string> = msgIds.split(",");
    let conversationMessagesTemp: Message[] = [
      ...conversationMessagesRef.current!,
    ];

    conversationMessagesTemp.map(async (element) => {
      if (messagesIds.includes(element?.message_id!)) {
        element.status = "0";
      }
    });

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const readMessage = () => {
    let conversationMessagesTemp: Message[] = [
      ...conversationMessagesRef.current!,
    ];

    conversationMessagesTemp.map(async (element) => {
      if (element.read_status === "0") {
        element.read_status = "1";
      }
    });

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesTemp);
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateTypingStatus = (val: string, flag: string) => {
    let status: boolean = false;
    if (flag === "1") {
      status = val === "1";
      setTypingStatus(status);
      setRecordingStatus(false);
    } else {
      status = val === "1";
      setTypingStatus(false);
      setRecordingStatus(status);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const conversationCallBack = async (eventName: string, ...args: any[]) => {
    console.log(
      `ConversationCallBack Department: ${eventName} flag: ${chatSelectedRef
        .current?.flag!}`,
      args
    );

    let hasConvId: boolean = false;
    let results: any = null;
    let resultConversationId: string = "";

    if (chatSelectedRef.current?.flag! === "2") {
      if (args.length > 0) {
        results = args[0]["results"] ?? null;
        hasConvId = results != null && "conv_id" in results;

        if (hasConvId) {
          resultConversationId = results["conv_id"];

          if (UtilityHelper.isNotEmpty(resultConversationId)) {
            if (eventName === SocketChatsKeys.chat_message_history) {
              const deptmentConversationMessageModel: GroupConversationMessageModel =
                args[0];

              initMessages(deptmentConversationMessageModel);

              /* let conversationMessagesTemp: Message[] =
                deptmentConversationMessageModel?.results?.messages!;

              conversationMessagesTemp = conversationMessagesTemp.filter(
                (m) => m.message_type !== "12"
              );

              conversationMessagesRef.current = conversationMessagesTemp;
              messageDecode(conversationMessagesRef.current!);

              await UtilityHelper.delay();
              SocketService.readMessage(
                deptmentConversationMessageModel?.results?.conv_id!,
                chatSelectedRef?.current?.flag!
              ); */

              //
            } else if (eventName === SocketChatsKeys.new_chat_message) {
              if (
                "message" in results &&
                "conv_id" in results &&
                results["conv_id"] === chatSelectedRef.current?.conv_id
              ) {
                const message: Message = results["message"];

                //conversationMessagesRef.current!.push(message);
                conversationMessagesRef.current = [
                  message,
                  ...conversationMessagesRef.current!,
                ];
                messageDecode(conversationMessagesRef.current!);

                if (userId !== message.account_id) {
                  await UtilityHelper.delay();
                  SocketService.readMessage(
                    resultConversationId,
                    chatSelectedRef?.current?.flag!
                  );
                }

                // RELOAD ONLY IN WEB LAYOUT
                if (type === 3) {
                  dispatch(chatRefreshListStatus(true));
                }
              }
              //
            } else if (eventName === SocketChatsKeys.profile_typing) {
              if ("typing_status" in results) {
                updateTypingStatus(
                  results["typing_status"],
                  results["typing_flag"]
                );
              }
              //
            } else if (eventName === SocketChatsKeys.mark_read_chat) {
              readMessage();
              //
            } else if (eventName === SocketChatsKeys.chat_block_profile) {
              if (
                "account_id" in results &&
                "profile_id" in results &&
                "participant_block_status" in results
              ) {
                updateBlockStatus({
                  participantBlockStatus: true,
                  accountIdVal: results["account_id"],
                  profileId: results["profile_id"],
                  participantBlockStatusVal:
                    results["participant_block_status"],
                });
              }
              //
            } else if (eventName === SocketChatsKeys.chat_delete_messages) {
              if ("message_id" in results) {
                updateDeleteStatusFromOther(results["message_id"]);
              }
              //
            }
          }
        }
      }
    }
  };

  const initMessages = async (response: GroupConversationMessageModel) => {
    let conversationMessagesTemp: Message[] = response?.results?.messages!;

    conversationMessagesTemp = conversationMessagesTemp.filter(
      (m) => m.message_type !== "12"
    );

    conversationMessagesRef.current = conversationMessagesTemp;
    messageDecode(conversationMessagesRef.current!);

    await UtilityHelper.delay();
    SocketService.readMessage(
      response?.results?.conv_id!,
      chatSelectedRef?.current?.flag!
    );
  };
  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const messageDecode = async (messages: Array<Message>) => {
    if (conversationDetailRef.current != null) {
      let messagesList: Array<Message> = [];

      await Promise.all(
        messages!.map(async (element, index) => {
          if (
            element?.message! != null &&
            UtilityHelper.isNotEmpty(element.message!)
          ) {
            element.decripted_mesage = await EncryptDecryptService.decryptGroup(
              {
                secretKey: privateKeyRef.current,
                cipherTextBase64: element.message!,
                ivBase64: element.message_iv!,
                tagBase64: element.message_tag!,
              }
            );
          }

          if (
            element.replay_status === "1" &&
            element.replay_message != null &&
            UtilityHelper.isNotEmpty(element?.replay_message!.message ?? "")
          ) {
            element.replay_message!.decripted_mesage = "";

            element.replay_message!.decripted_mesage =
              await EncryptDecryptService.decryptGroup({
                secretKey: privateKeyRef.current,
                cipherTextBase64: element.replay_message!.message!,
                ivBase64: element.replay_message!.message_iv!,
                tagBase64: element.replay_message!.message_tag!,
              });
          }

          messagesList.push(element);
        })
      );

      messagesList = [...messagesList].sort(
        (a, b) => Number(b.message_id) - Number(a.message_id)
      );

      let messagesListGrouped: Record<string, Message[]> =
        DepartmentChatsProvider.groupByDate(messagesList);

      messagesListGrouped = Object.fromEntries(
        Object.entries(messagesListGrouped).reverse()
      );

      /* const sortedEntries = Object.entries(messagesListGrouped).sort(
        ([dateA], [dateB]) =>
          new Date(dateA).getTime() - new Date(dateB).getTime()
      );

      messagesListGrouped = {};
      sortedEntries.map((item) => {
        console.log(item[1]);
        messagesListGrouped[item[0]] = item[1];
      }); */

      setConversationMessages(messagesListGrouped);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateConversationSettings = ({
    convId,
    eventName,
    exitFlag = "",
    muteStatus = "",
    muteDuration = "",
    muteType = "",
    profileId = "",
    blockStatus = "",
  }: UpdateConversationSettingsParams) => {
    console.log(`convId: ${convId}, eventName: ${eventName}`);

    if (
      UtilityHelper.isNotEmpty(convId) &&
      UtilityHelper.isNotEmpty(eventName)
    ) {
      if (eventName === SocketChatsKeys.chat_clear_messages) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_clear_messages,
          callback: updateClearChatDate,
        });
      } else if (eventName === SocketChatsKeys.chat_block_profile) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_block_profile,
          profileId: profileId,
          blockStatus: blockStatus,
          callback: (response: any) => {
            updateBlockStatus({
              participantBlockStatus: false,
              accountIdVal: response["results"]["account_id"],
              profileId: response["results"]["profile_id"],
              blockStatusVal: response["results"]["my_block_status"],
            });
          },
        });
      } else if (eventName === SocketChatsKeys.chat_mute_notification) {
        SocketService.updateConversationSettings({
          convId: chatSelectedRef.current?.conv_id!,
          flag: chatSelectedRef.current?.flag!,
          eventN: SocketChatsKeys.chat_mute_notification,
          muteDuration: muteDuration,
          muteStatus: muteStatus,
          muteType: muteType,
          callback: async (response: any) => {
            conversationDetailRef.current = {
              ...conversationDetailRef.current,
              conv_settings: {
                ...conversationDetailRef.current?.conv_settings,
                muted_conversations: {
                  ...conversationDetailRef.current?.conv_settings
                    ?.muted_conversations!,
                  mute_status: response["results"]["mute_status"],
                  mute_type: response["results"]["mute_type"],
                  mute_start: response["results"]["mute_start"],
                  mute_duration: response["results"]["mute_duration"],
                  mute_end: response["results"]["mute_end"],
                },
              },
            };

            setConversationDetail(conversationDetailRef.current);
            updateMuteSettings();

            // RELOAD ONLY IN WEB LAYOUT
            if (type === 3) {
              dispatch(chatRefreshListStatus(true));
            }

            await UtilityHelper.delay(200);
            dispatch(removeModal(ModalKeys.chat_mute_notification));
          },
        });
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const updateClearChatDate = (response: any) => {
    const clearChatDateVal: string =
      response["results"]["clear_chat_date"] ?? "";

    if (UtilityHelper.isNotEmpty(clearChatDateVal)) {
      if (
        conversationDetailRef.current?.conv_settings?.clear_chat_date != null
      ) {
        conversationDetailRef.current = {
          ...conversationDetailRef.current,
          conv_settings: {
            ...conversationDetailRef.current?.conv_settings,
            clear_chat_date: clearChatDateVal,
          },
        };

        setConversationDetail(conversationDetailRef.current);

        conversationMessagesRef.current = [];
        setConversationMessages({});
      }
    }
  };

  /// SEND MESSAGE  //////////////////////////////////////////////////////////////////////////

  const sendTextMessage = (textMessage: string, callback: () => void) => {
    console.log(`sendTextMessage`);
    if (socket.connected && UtilityHelper.isNotEmpty(textMessage)) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "1",
        status: "0",
      });

      const txtType: ChatTextType = ChatsProvider.identifyTextType(textMessage);

      sendMessage({
        msg: textMessage,
        msgType: txtType === ChatTextType.url ? "9" : "1",
        callback: () => {
          callback();
        },
      });
    }
  };

  const sendLocation = (textMessage: string) => {
    sendMessage({
      msg: textMessage,
      msgType: "5",
      callback: () => {},
    });
  };

  const sendAudioMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendAudioMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      SocketService.typingChatMessage({
        convId: chatSelectedRef.current?.conv_id!,
        flag: "2",
        status: "0",
      });

      sendMediaMessage(file, durationSeconds, callback);
    }
  };

  const sendMediaMessage = async (
    file: File,
    durationSeconds: number,
    callback: () => void
  ) => {
    console.log(`sendMediaMessage`);
    if (socket.connected) {
      // GETTING CHAT DETAIL
      getSelectedChatSession(false);

      voiceLengthRef.current = UtilityHelper.formatTimer(
        Math.ceil(durationSeconds)
      );

      Loader.modal();
      const detailUploaded: UploadEncryptedMedia | null =
        await ImagePickerPreviewProvider.uploadMediaChatFile({
          convId: chatSelectedRef.current?.conv_id!,
          mediaFile: file,
          mediaFileName: "image.png",
        });
      Loader.modal(false);

      if (detailUploaded != null) {
        const urlArr: string[] | undefined =
          detailUploaded.file_url?.split(".");
        const fileExt: string = urlArr![urlArr!.length - 1];

        let msg: string = "";

        const msgType: string = imageExtensions.includes(fileExt)
          ? "2"
          : audioExtensions.includes(fileExt)
          ? "3"
          : docExtensions.includes(fileExt)
          ? "6"
          : videoExtensions.includes(fileExt)
          ? "4"
          : pdfExtensions.includes(fileExt)
          ? "7"
          : "8";

        if (msgType === "3") {
          msg = `${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (msgType === "4") {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${voiceLengthRef.current}`;
        } else if (["6", "7", "8"].includes(msgType)) {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}|${detailUploaded.original_name}`;
        } else {
          msg = `|${detailUploaded.file_url}|${detailUploaded.file_type}|${detailUploaded.file_size}`;
        }

        sendMessage({
          msg: msg,
          msgType: msgType,
          fileId: detailUploaded.file_id,
          callback: () => {
            callback();
          },
        });
      }
    }
  };

  const sendMessage = async ({
    msg,
    msgType,
    fileId = "",
    replyId = "",
    forwardStatus = "0",
    forwardCovId = "",
    forwardAccId = "",
    forwardKey = "",
    forwardChatFlag = "",
    callback,
  }: {
    msg: string;
    msgType: string;
    fileId?: string;
    replyId?: string;
    forwardStatus?: string;
    forwardCovId?: string;
    forwardAccId?: string;
    forwardKey?: string;
    forwardChatFlag?: string;
    callback?: (res?: any) => void;
  }) => {
    if (UtilityHelper.isNotEmpty(msg) && userId !== "0") {
      if (replyMessage != null) {
        replyId = replyMessage?.message_id!;
      }

      if (
        UtilityHelper.isNotEmpty(forwardChatFlag) &&
        ["1"].includes(forwardChatFlag) &&
        forwardStatus === "1"
      ) {
        const publickKey: string =
          CacheStorage.getValue(StorageKeys.public_key) || "";

        if (msg.length < ChatsProvider.messageMaxLength) {
          const senEncMsg: string | null =
            EncryptDecryptService.encryptWithPublicKey(msg, publickKey);

          const recEncMsg: string | null =
            EncryptDecryptService.encryptWithPublicKey(msg, forwardKey);

          if (senEncMsg != null && recEncMsg != null) {
            const dataMessage: Record<string, string> = {
              [userId]: senEncMsg,
              [forwardAccId]: recEncMsg,
            };

            SocketService.sendTeamMessage({
              flag: chatSelectedRef.current?.flag!,
              convId: forwardCovId,
              message: dataMessage,
              messageType: msgType,
              forwardStatus: forwardStatus,
              fileId: fileId,
              replyId: replyId,
              callback: () => {
                if (callback !== undefined) {
                  callback();
                }

                sendAutoReplyMessage();
                clearLongPress();
                cancelReply();
              },
            });
          }
        } else {
          const senEncMsg: HybridEncrypted | null =
            EncryptDecryptService.hybridEncrypt(msg, publickKey);

          const recEncMsg: HybridEncrypted | null =
            EncryptDecryptService.hybridEncrypt(msg, forwardKey);

          if (senEncMsg != null && recEncMsg != null) {
            const dataMessage: Record<string, HybridEncrypted> = {
              [userId]: senEncMsg,
              [forwardAccId]: recEncMsg,
            };

            SocketService.sendTeamMessage({
              flag: chatSelectedRef.current?.flag!,
              convId: forwardCovId,
              message: {},
              hybridMessage: dataMessage,
              messageType: msgType,
              forwardStatus: forwardStatus,
              fileId: fileId,
              replyId: replyId,
              callback: () => {
                sendAutoReplyMessage();
                clearLongPress();

                if (callback !== undefined) {
                  callback();
                }
              },
            });
          }
        }
      } else {
        const encVal: GroupMessageEncryptedType | null =
          await EncryptDecryptService.encryptGroup(
            forwardStatus === "1" ? forwardKey : privateKeyRef.current,
            msg
          );

        if (encVal != null) {
          SocketService.sendDeptMessage({
            flag: chatSelectedRef.current?.flag!,
            convId:
              forwardStatus === "1"
                ? forwardCovId
                : chatSelectedRef.current?.conv_id!,
            message: encVal.cipherText,
            messageIv: encVal.iv,
            messageTag: encVal.tag,
            messageType: msgType,
            forwardStatus: forwardStatus,
            fileId: fileId,
            replyId: replyId,
            callback: () => {
              if (callback !== undefined) {
                callback();
              }

              sendAutoReplyMessage();
              clearLongPress();
              cancelReply();
            },
          });
        }
      }
    }
  };

  const sendAutoReplyMessage = async () => {
    if (
      conversationDetailRef.current?.conv_settings?.receiver_auto_reply_details
        ?.status === "1"
    ) {
      const msg: string =
        conversationDetailRef.current?.conv_settings
          ?.receiver_auto_reply_details?.message!;
      const encVal: GroupMessageEncryptedType | null =
        await EncryptDecryptService.encryptGroup(privateKeyRef.current, msg);

      if (encVal != null) {
        SocketService.sendDeptMessage({
          flag: chatSelectedRef.current?.flag!,
          convId: chatSelectedRef.current?.conv_id!,
          message: encVal.cipherText,
          messageIv: encVal.iv,
          messageTag: encVal.tag,
          messageType: "13",
          callback: () => {},
        });
      }
    }
  };

  const submittedARFDetailChat = () => {
    SocketService.arfDepartmentMessages({
      convId: chatSelectedRef.current?.conv_id!,
      callback: async (resultList: any) => {
        dispatch(chatRefreshListStatus(true));

        const deptmentConversationMessageModel: GroupConversationMessageModel =
          resultList;

        initMessages(deptmentConversationMessageModel);
      },
    });
  };

  const cancelARF = async (groupId: string) => {
    if (UtilityHelper.isNotEmpty(groupId)) {
      getSelectedChatSession(false);

      Loader.modal();
      const statusRes: boolean = await DepartmentChatsProvider.cancelARF(
        groupId,
        chatSelectedRef.current?.conv_id!
      );
      Loader.modal(false);

      if (statusRes) {
        submittedARFDetailChat();
      }
    }
  };

  const deleteMessages = (flagId: "0" | "1") => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      updateDeleteStatus(flagId);
    }
  };

  const deleteMediaMessages = (flagId: "0" | "1") => {
    if (mediaDeleteEveryoneSelector != null) {
      if ((mediaDeleteEveryoneSelector?.messagesId ?? []).length > 0) {
        selectedMesasgeIdsRef.current = mediaDeleteEveryoneSelector?.messagesId;

        updateDeleteStatus(flagId);

        // REMOVE MODAL
        if (chatSelectedRef.current?.flag !== "3") {
          dispatch(removeModal(ModalKeys.chat_media));
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
    }
  };

  const updateDeleteStatus = (flagId: "0" | "1") => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      SocketService.deleteMessage({
        flag: chatSelectedRef.current?.flag!,
        convId: chatSelectedRef.current?.conv_id!,
        msgIDs: selectedMesasgeIdsRef.current.join(","),
        status: flagId,
        callback: (response: any) => {
          updateDeleteStatusFromOther(selectedMesasgeIdsRef.current.join(","));
          clearLongPress();
        },
      });
    }
  };

  const sendForwardMessageList = async (
    profileConvers: Array<ForwardMessageList>
  ) => {
    console.log("sendForwardMessageList");
    if (forwardedMediaListSelector != null && profileConvers.length > 0) {
      Loader.modal();
      await Promise.all(
        profileConvers.map(async (conv) => {
          forwardedMediaListSelector.map(async (msg) => {
            await sendMessage({
              msg: msg.decripted_mesage!,
              msgType: msg.message_type!,
              fileId: msg.file_id!,
              forwardStatus: "1",
              forwardCovId: conv.conv_id!,
              forwardAccId: conv.account_id!,
              forwardKey: conv.key!,
              forwardChatFlag: conv.flag,
              callback: () => {},
            });
          });
        })
      );
      Loader.modal(false);
    } else {
      if (
        selectedMesasgeIdsRef.current.length > 0 &&
        profileConvers.length > 0
      ) {
        Loader.modal();
        await Promise.all(
          profileConvers.map(async (conv) => {
            selectedMessages.map(async (msg) => {
              //if (["1", "5", "9"].includes(msg.message_type!)) {
              await sendMessage({
                msg: msg.decripted_mesage!,
                msgType: msg.message_type!,
                fileId: msg.file_id!,
                forwardStatus: "1",
                forwardCovId: conv.conv_id!,
                forwardAccId: conv.account_id!,
                forwardKey: conv.key!,
                forwardChatFlag: conv.flag,
                callback: () => {},
              });
              // }
            });
          })
        );
        Loader.modal(false);
      }
    }

    console.log("clear forward");

    UtilityHelper.delay(100);
    dispatch(removeModal(ModalKeys.chat_forward));
    dispatch(removeModal(ModalKeys.chat_forward_media));
    clearLongPress();
    cancelReply();

    // CLEAR MEDIA SELECTION
    dispatch(mediaDeleteChooseAction(null));
    dispatch(setForwardMediaList(null));

    // RELOAD ONLY IN WEB LAYOUT
    if (type === 3) {
      dispatch(chatRefreshListStatus(true));
    } else {
      window.history.replaceState(null, "", window.location.pathname);
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const handlePressStart = (msdDetail: Message) => {
    timerRef.current = setTimeout(() => {
      longPressStatusRef.current = true;
      setLongPressStatus(true);
      console.log("Long press detected!");

      if (isMobile) {
        toggleMessageIdSelection(msdDetail);
      }
    }, 500);
  };

  const handlePressEnd = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    console.log("Press and hold");
  };

  const handleClickMessage = (msdDetail: Message) => {
    console.log(`handleClickMessage: ${longPressStatusRef.current}`);
    if (longPressStatusRef.current) {
      toggleMessageIdSelection(msdDetail);

      if (selectedMesasgeIdsRef.current.length === 0) {
        clearLongPress();
      }
    }
  };

  const toggleMessageIdSelection = (msdDetail: Message) => {
    if (
      msdDetail.status === "1" &&
      !msdDetail.deleted?.includes(userId) &&
      !chatsARFMessageTypes.includes(msdDetail.message_type!)
    ) {
      let ids = [...selectedMesasgeIdsRef.current];
      const id = msdDetail?.message_id!;

      if (ids.includes(id)) {
        ids = ids.filter((item) => item !== id);
      } else {
        ids.push(id);
      }

      selectedMesasgeIdsRef.current = ids;
      setSelectedMesasgeIds(ids);

      const messsageList: Array<Message> =
        conversationMessagesRef.current!.filter((msg) =>
          ids.includes(msg.message_id!)
        );
      setSelectedMessages(messsageList);
    }
  };

  const longPressAction = async (
    action: "reply" | "copy" | "delete" | "forward"
  ) => {
    if (selectedMesasgeIdsRef.current.length > 0) {
      console.log(`longPressAction: ${action}`);
      const messageSelected: Message = selectedMessages[0];
      const chatDecryptedMessage: string =
        messageSelected.decripted_mesage ?? "";

      if (action === "reply") {
        setReplyMessage({
          message_id: messageSelected.message_id,
          account_id: messageSelected.account_id,
          name: messageSelected.name,
          message_type: messageSelected.message_type,
          decripted_mesage: chatDecryptedMessage,
        });
        clearLongPress();
      } else if (action === "copy") {
        let copyMsg: string = "";
        switch (messageSelected.message_type) {
          case "1":
          case "9":
            copyMsg = chatDecryptedMessage;
            break;
          case "2":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "3":
            copyMsg = chatDecryptedMessage.split("|")[0];
            break;
          case "4":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
          case "5":
            copyMsg = `https://maps.google.com/?q=${
              chatDecryptedMessage.split("|")[1]
            },${chatDecryptedMessage.split("|")[2]}`;
            break;
          case "6":
          case "7":
          case "8":
            copyMsg = chatDecryptedMessage.split("|")[1];
            break;
        }

        clearLongPress();

        try {
          await navigator.clipboard.writeText(copyMsg);
          ShowToast.success(AppTranslation.text("text_copied_to_clipboard"));
        } catch (err) {
          ShowToast.error("Failed to copy!");
          console.error("Failed to copy!", err);
        }
      } else if (action === "delete") {
        dispatch(
          addModal({
            id: ModalKeys.chat_delete_message,
            title:
              selectedMesasgeIdsRef.current.length > 1
                ? "delete_messages"
                : "delete_message",
            titleCenter: true,
            allowBottomSheet: true,
            size: "md",
          })
        );
      } else if (action === "forward") {
        //clearLongPress(); // keep the mesages
        dispatch(
          addModal({
            id: ModalKeys.chat_forward,
            title: "forward",
            size: "lg",
            screenMobileView: type === 3 ? false : true,
          })
        );
      }
    }
  };

  /////////////////////////////////////////////////////////////////////////////////////////////////////////
  const disconnectCallBacks = () => {
    if (socket.connected) {
      SocketService.disconnectCallBacks(conversationCallBack);
      console.log(`socket disconnect - DepartmentChatsContextProvider`);
      //SocketService.disconnect();
      SocketService.leaveChat();
    }
  };

  useEffect(() => {
    if (
      muteDurationDepartmentSelector != null &&
      UtilityHelper.isNotEmpty(muteDurationDepartmentSelector)
    ) {
      submitMuteDetail(muteDurationDepartmentSelector);
    }
    // eslint-disable-next-line
  }, [muteDurationDepartmentSelector]);

  useEffect(() => {
    if (arfFormDetailSelector != null) {
      submittedARFDetailChat();
      dispatch(submitARFDetail(null));
    }

    return () => {
      dispatch(submitARFDetail(null));
    };
    // eslint-disable-next-line
  }, [arfFormDetailSelector]);

  // MY MESSAGES
  useEffect(() => {
    if (sendMessageDetailSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "2") {
        if (sendMessageDetailSelector.messageType === "1") {
          sendTextMessage(sendMessageDetailSelector.message, () => {});
        } else if (sendMessageDetailSelector.messageType === "5") {
          sendLocation(sendMessageDetailSelector.message);
        }
      }
      dispatch(sendChatMessage(null));
    }
    // eslint-disable-next-line
  }, [sendMessageDetailSelector]);

  // ARF FORM MESSAGE CANCEL
  useEffect(() => {
    if (cancelARFDetailSelector != null) {
      cancelARF(cancelARFDetailSelector?.groupId!);
      dispatch(cancelChatARF(null));
    }
    // eslint-disable-next-line
  }, [cancelARFDetailSelector]);

  // DELETE MESSAGES
  useEffect(() => {
    if (messageDeleteActionTypeSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "2") {
        deleteMessages(messageDeleteActionTypeSelector);
      }
      dispatch(messageDeleteChooseAction(null));
    }
    // eslint-disable-next-line
  }, [messageDeleteActionTypeSelector]);

  // DELETE MEDIA
  useEffect(() => {
    if (mediaDeleteActionTypeSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "2") {
        deleteMediaMessages(mediaDeleteActionTypeSelector);
      }
      dispatch(mediaDeleteChooseAction(null));
    }
    // eslint-disable-next-line
  }, [mediaDeleteActionTypeSelector]);

  // FORWARD MESSAGES
  useEffect(() => {
    if (forwardedMessagesSelector != null) {
      getSelectedChatSession(false);

      if (chatSelectedRef.current?.flag === "2") {
        sendForwardMessageList(forwardedMessagesSelector);
      }
      dispatch(sendForwardMessages(null));
    }
    // eslint-disable-next-line
  }, [forwardedMessagesSelector]);

  useEffect(() => {
    resetValues();
    // eslint-disable-next-line
  }, []);

  return (
    <DepartmentChatsContext.Provider
      value={{
        getSelectedChatSession,
        resetMessages,
        conversationCallBack,
        disconnectCallBacks,
        updateConversationSettings,
        updateMuteSettings,

        userId,
        conversationMessages,
        conversationDetail,
        muteSettings,

        typingStatus,
        recordingStatus,

        // CHATTING
        sendTextMessage,
        sendAudioMessage,
        sendMediaMessage,

        // OPTION
        handleClickMessage,
        handlePressStart,
        handlePressEnd,
        clearLongPress,
        selectedMesasgeIds,
        selectedMessages,
        longPressStatus,

        replyMessage,
        longPressAction,
        cancelReply,
      }}
    >
      {children}
    </DepartmentChatsContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const DepartmentChatsContextImport = () => {
  const context = useContext(DepartmentChatsContext);
  if (!context) {
    throw new Error(
      "DepartmentChatsContextImport must be used within a DepartmentChatsContextProvider"
    );
  }
  return context;
};
